import { samePoint, usablePoint, type LatLng } from '@agos/shared'

/**
 * Where a delivery goes, held beside the address text - FRONTEND.md §9.693, FB-1003, the owner's #13.
 *
 * **The address box has resolved a point since 2026-08-03 and the order threw it away.** `NewOrderPage`
 * used the picked suggestion's coordinates to guess the zone and never passed them to
 * `buildOrderPayload`, and an edit had no field for them at all - so every phone order was stored with
 * no `delivery_lat` / `delivery_lon`, and the driver's *Navigovať* (`navigationUrl`, §9.504) fell back to
 * searching the text. Legacy sent them on both paths (`new-order.tsx:943-944`, `:1024-1025`).
 *
 * The rules for the point, in one place so the page only says *when*:
 *
 *   - a picked suggestion, §9.694's pin and the loaded order **set** it;
 *   - typing the address **clears** it - legacy `new-order.tsx:1219` - because a point that no longer
 *     describes the text sends the driver somewhere the text does not say;
 *   - **except** typing the address back to exactly what the loaded order holds, which restores the
 *     loaded point: touching the box and undoing it must not erase a point that is stored.
 */

/** The order's own point, or `null` unless both coordinates are numbers. */
export function loadedPointOf(
  order: { deliveryLat: number | null; deliveryLon: number | null } | null,
): LatLng | null {
  if (order === null) return null
  return usablePoint({ lat: order.deliveryLat, lng: order.deliveryLon })
}

/** What point stays behind an address that was just typed. */
export function pointForTypedAddress(
  typed: string,
  loaded: { customerAddress: string; deliveryLat: number | null; deliveryLon: number | null } | null,
): LatLng | null {
  if (loaded === null || typed !== loaded.customerAddress) return null
  return loadedPointOf(loaded)
}

/**
 * Both absent, or the same two numbers - in `@agos/shared` since §9.705, because the checkout's pin map asks it
 * too. Re-exported so this module's importers are unchanged.
 */
export { samePoint }

/**
 * What an edit says about the point - §9.693.
 *
 * **Nothing when nothing moved**: a patch that names a field rewrites it, and `PATCH /orders/{id}`
 * tells *absent* from *null* precisely so a caller can say nothing. **The new point when there is
 * one**, and **`null` for both when the address changed without one** - `OrderEditController` reads a
 * present null as *clear it*. Never one coordinate without the other.
 */
export function coordinatesPatch(
  current: LatLng | null,
  loaded: LatLng | null,
): { deliveryLat: number | null; deliveryLon: number | null } | null {
  if (samePoint(current, loaded)) return null
  if (current === null) return { deliveryLat: null, deliveryLon: null }
  return { deliveryLat: current.lat, deliveryLon: current.lng }
}
